import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["name", "imageSrc", "linkMode", "contentHref", "tags", "onLinkClick", "contentType", "lengthType", "length", "progress", "awardLink", "cardMenu", "ribbonText", "ribbonUse", "videoPlayer", "flush", "footer", "description", "thumbnailOverlay", "isGhost", "menuAlwaysVisible", "isInternalContent", "selected", "onThumbnailLoad", "cardRef"];
let _ = t => t,
  _t,
  _t2;
import { SuccessIcon } from 'foundations-assets/icons/components/SuccessIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { Image } from 'foundations-components/transitional/components/Image';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import styledWithTheme, { useTheme } from 'foundations-theming/utils';
import { Fragment, useState } from 'react';
import { PolymorphicLink } from '../PolymorphicLink';
import { List, ListItem } from 'foundations-components/transitional/components/List';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { NanoProgress } from 'foundations-components/transitional/components/NanoProgress';
import FormattedDuration from 'I18n/components/FormattedDuration';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { StatusDot } from 'foundations-components/transitional/components/StatusDot';
import { Ribbon } from 'foundations-components/transitional/components/Ribbon';
import { ContentTypeBadgeMap, ClampedText } from './shared';
import ACDynamicThumbnailImage from './ACDynamicThumbnailImage';
import { stringToHash } from '../../utils/math';
import { useMediaQuery } from '../../utils/Styling';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const ImageContainer = styledWithTheme.div(_t || (_t = _`
  width: 100%;
  aspect-ratio: 16 / 9;
  background-color: ${0};
  border-radius: ${0};
  overflow: hidden;
  margin-block-end: ${0};
  position: relative;
  border-end-end-radius: 0;
  border-end-start-radius: 0;
`), ({
  theme
}) => theme.color['fill-surface-recessed'], ({
  theme
}) => theme.borderRadius['300'], ({
  theme
}) => theme.space['400']);
const TransparentContainer = styledWithTheme.div(_t2 || (_t2 = _`
  opacity: 0.5;
`));
export default function ACContentCardLarge(_ref) {
  let {
      name,
      imageSrc,
      linkMode = 'default',
      contentHref,
      tags = [],
      onLinkClick,
      contentType,
      lengthType = 'time',
      length,
      progress = 0,
      awardLink,
      cardMenu,
      ribbonText: ribbonTextProp,
      ribbonUse: ribbonUseProp = 'new',
      videoPlayer,
      flush = false,
      footer,
      description,
      thumbnailOverlay,
      isGhost,
      menuAlwaysVisible,
      isInternalContent,
      selected,
      onThumbnailLoad,
      cardRef
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const theme = useTheme();
  const [isHovering, setIsHovering] = useState(false);
  const {
    isTouchscreen
  } = useMediaQuery();
  const ribbonText = isInternalContent ? /*#__PURE__*/_jsx(FormattedMessage, {
    message: "academyCommon.contentCard.badge.internal"
  }) : ribbonTextProp;
  const ribbonUse = isInternalContent ? 'norman' : ribbonUseProp;
  const linkProps = {
    href: contentHref,
    linkMode,
    onClick: onLinkClick,
    use: 'unstyled'
  };
  const MaybeTransparentContainer = isGhost ? TransparentContainer : Fragment;
  return /*#__PURE__*/_jsxs(CardWrapper, Object.assign({
    borderColor: selected ? 'border-interactive-pressed' : undefined,
    borderRadius: "300",
    borderStyle: selected ? 'solid' : undefined,
    borderWidth: selected ? '200' : undefined,
    compact: true,
    "data-test-id": "academy-content-card-large",
    marginBlockEnd: flush ? '0' : undefined
    // This creates a new stacking context, so the card menu can be on top of the card, but not over other elements outside the card
  }, props, {
    onMouseEnter: e => {
      var _props$onMouseEnter;
      setIsHovering(true);
      (_props$onMouseEnter = props.onMouseEnter) === null || _props$onMouseEnter === void 0 || _props$onMouseEnter.call(props, e);
    },
    onMouseLeave: e => {
      var _props$onMouseLeave;
      setIsHovering(false);
      (_props$onMouseLeave = props.onMouseLeave) === null || _props$onMouseLeave === void 0 || _props$onMouseLeave.call(props, e);
    },
    ref: cardRef,
    zIndex: 0,
    children: [ribbonText && /*#__PURE__*/_jsx(Ribbon, {
      position: "left",
      use: ribbonUse,
      zIndex: "11",
      children: ribbonText
    }), /*#__PURE__*/_jsxs(PolymorphicLink, Object.assign({
      blockSize: "100%",
      display: "flex",
      flexDirection: "column"
    }, linkProps, {
      tabIndex: -1,
      children: [/*#__PURE__*/_jsxs(ImageContainer, {
        children: [/*#__PURE__*/_jsx(MaybeTransparentContainer, {
          children: imageSrc ? /*#__PURE__*/_jsx(Image, {
            alt: name,
            blockSize: "100%",
            inlineSize: "100%",
            loading: "lazy",
            objectFit: "cover",
            onLoad: onThumbnailLoad,
            src: imageSrc
          }) : /*#__PURE__*/_jsx(ACDynamicThumbnailImage, {
            seed: stringToHash(name + contentType + contentHref),
            title: name,
            type: "rectangle"
          })
        }), thumbnailOverlay && /*#__PURE__*/_jsx(View, {
          inset: "0",
          position: "absolute",
          children: thumbnailOverlay
        }), /*#__PURE__*/_jsx(MaybeTransparentContainer, {
          children: /*#__PURE__*/_jsx(View, {
            blockSize: "6px",
            insetBlockEnd: "0",
            insetInlineEnd: "0",
            insetInlineStart: "0",
            paddingInline: "300",
            position: "absolute",
            children: /*#__PURE__*/_jsx(NanoProgress, {
              color: theme.color['fill-accent-teal-default'],
              height: 6,
              value: progress / length * 100
            })
          })
        }), videoPlayer && /*#__PURE__*/_jsx(View, {
          inset: "0",
          position: "absolute",
          zIndex: "10",
          children: videoPlayer
        }), cardMenu && /*#__PURE__*/_jsx(View, {
          insetBlockStart: "16px",
          insetInlineEnd: "16px",
          position: "absolute",
          visibility: menuAlwaysVisible || isHovering || isTouchscreen ? 'visible' : 'hidden',
          zIndex: "100",
          children: cardMenu
        })]
      }), /*#__PURE__*/_jsx(MaybeTransparentContainer, {
        children: /*#__PURE__*/_jsxs(Flex, {
          flex: 1,
          flexDirection: "column",
          gap: "200",
          paddingBlockEnd: "400",
          paddingInline: "400",
          children: [/*#__PURE__*/_jsx(Tooltip, {
            delay: 500,
            title: name,
            children: /*#__PURE__*/_jsx(View, {
              blockSize: "36px",
              children: /*#__PURE__*/_jsx(Text, {
                as: "h6",
                overflowWrap: "break-word",
                children: /*#__PURE__*/_jsx(ClampedText, {
                  underlineOnHover: true,
                  children: name
                })
              })
            })
          }), description && /*#__PURE__*/_jsx(Text, {
            as: "small",
            color: "text-core-subtle",
            children: description
          }), footer || /*#__PURE__*/_jsxs(_Fragment, {
            children: [/*#__PURE__*/_jsx(Text, {
              as: "small",
              color: "text-core-subtle",
              children: /*#__PURE__*/_jsxs(List, {
                use: "inline-divided",
                children: [contentType && /*#__PURE__*/_jsx(ListItem, {
                  children: ContentTypeBadgeMap[contentType]
                }), !!length && /*#__PURE__*/_jsx(ListItem, {
                  children: lengthType === 'items' ? /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "academyCommon.contentCard.itemCount",
                    options: {
                      count: length
                    }
                  }) : /*#__PURE__*/_jsx(FormattedDuration, {
                    from: Date.now() - length * 1000
                  })
                })]
              })
            }), !progress ? /*#__PURE__*/_jsx(Flex, {
              alignItems: "end",
              flexWrap: "wrap",
              gap: "200",
              marginBlockStart: "auto",
              children: tags.map((tag, index) => /*#__PURE__*/_jsx(Badge, {
                use: "koala",
                children: tag
              }, index))
            }) : progress === length ? /*#__PURE__*/_jsxs(Flex, {
              flexWrap: "wrap",
              gap: "200",
              justifyContent: "space-between",
              children: [/*#__PURE__*/_jsxs(Flex, {
                alignItems: "center",
                gap: "100",
                children: [/*#__PURE__*/_jsx(SuccessIcon, {
                  color: "fill-positive-default"
                }), /*#__PURE__*/_jsx(Text, {
                  as: "small",
                  children: ['VIDEO', 'ACADEMY_VIDEO'].includes(contentType !== null && contentType !== void 0 ? contentType : '') ? /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "academyCommon.progress.watched"
                  }) : /*#__PURE__*/_jsx(FormattedMessage, {
                    message: "academyCommon.progress.completed"
                  })
                })]
              }), awardLink && /*#__PURE__*/_jsx(Text, {
                as: "small",
                children: awardLink
              })]
            }) : /*#__PURE__*/_jsxs(Text, {
              as: "small",
              children: [/*#__PURE__*/_jsx(StatusDot, {
                use: "active"
              }), lengthType === 'items' ? /*#__PURE__*/_jsx(FormattedMessage, {
                message: "academyCommon.contentCard.itemsRemaining",
                options: {
                  count: length - progress
                }
              }) : /*#__PURE__*/_jsx(FormattedReactMessage, {
                message: "academyCommon.tracks.timeRemaining.duration",
                options: {
                  duration: /*#__PURE__*/_jsx(FormattedDuration, {
                    from: Date.now() - (length - progress) * 1000
                  })
                }
              })]
            })]
          })]
        })
      })]
    }))]
  }));
}